import React, { useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/Layout';
import LanguageSelector from '../components/inputs/LanguageSelector';
import { ROLES } from '../constants/roles';
import useCompanySettings from '../components/settings/useCompanySettings';
import CompanySettingsSection from '../components/settings/CompanySettingsSection';
import VatSettingsSection from '../components/settings/VatSettingsSection';
import CurrencySettingsSection from '../components/settings/CurrencySettingsSection';
import ClientManagementSettingsSection from '../components/settings/ClientManagementSettingsSection';
import InventorySettingsSection from '../components/settings/InventorySettingsSection';
import SpecialRequestsSettingsSection from '../components/settings/SpecialRequestsSettingsSection';
import EmailNotificationsSettingsSection from '../components/settings/EmailNotificationsSettingsSection';
import { MANAGER_SETTINGS_SECTIONS, shouldRedirectLanguageSection, shouldRedirectClientPoliciesSection, getSettingsProfilePath, getClientManagementPath, getInventorySettingsPath } from '../utils/settingsForm';

const FieldRow = ({ label, value, emptyValue }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', py: 0.75, gap: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value || emptyValue}
    </Typography>
  </Box>
);

const Settings = () => {
  const { user, company, activeCompany, isInternal, updateUserLanguage } = useAuth();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const isManager = user?.role === ROLES.MANAGER;
  const companySettings = useCompanySettings();

  const activeSection = searchParams.get('section') || 'profile';

  useEffect(() => {
    if (shouldRedirectLanguageSection(searchParams.get('section'))) {
      navigate(getSettingsProfilePath(), { replace: true });
    }
    if (shouldRedirectClientPoliciesSection(searchParams.get('section'))) {
      navigate(getClientManagementPath('policies'), { replace: true });
    }
    if (searchParams.get('section') === 'clientManagement' && !searchParams.get('tab')) {
      navigate(getClientManagementPath('policies'), { replace: true });
    }
    if (searchParams.get('section') === 'inventory' && !searchParams.get('tab')) {
      navigate(getInventorySettingsPath('lots'), { replace: true });
    }
  }, [searchParams, navigate]);

  const handleLanguageChange = useCallback(async (lang) => {
    setLanguage(lang);
    updateUserLanguage(lang);
    try {
      await axios.put('/api/users/me/language', { language: lang });
    } catch (err) {
      console.error('Failed to save language preference', err);
    }
  }, [setLanguage, updateUserLanguage]);

  const renderProfileSection = () => (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
        <Typography variant="h6" fontWeight={700}>{t('settings.profile')}</Typography>
      </Box>
      <Divider />
      <Box sx={{ px: 3, py: 2.5 }}>
        <FieldRow label={t('settings.firstName')} value={user?.firstName} emptyValue={t('common.emptyValue')} />
        <FieldRow label={t('settings.lastName')} value={user?.lastName} emptyValue={t('common.emptyValue')} />
        <FieldRow label={t('settings.email')} value={user?.email} emptyValue={t('common.emptyValue')} />
        <FieldRow label={t('settings.companyLabel')} value={activeCompany?.companyName || company?.companyName} emptyValue={t('common.emptyValue')} />
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.75, gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
            {t('settings.selectLanguage')}
          </Typography>
          <LanguageSelector value={language} onChange={handleLanguageChange} />
        </Box>
      </Box>
    </Paper>
  );

  const renderManagerSection = () => {
    const shared = {
      form: companySettings.form,
      setField: companySettings.setField,
      setAddressField: companySettings.setAddressField,
      onSave: companySettings.save,
      onReset: companySettings.reset,
      loading: companySettings.loading,
      saving: companySettings.saving,
      error: companySettings.error,
      success: companySettings.success,
      setSuccess: companySettings.setSuccess,
    };

    switch (activeSection) {
      case 'company':
        return <CompanySettingsSection {...shared} />;
      case 'vat':
        return <VatSettingsSection {...shared} />;
      case 'currency':
        return <CurrencySettingsSection {...shared} />;
      case 'inventory':
        return <InventorySettingsSection {...shared} />;
      case 'specialRequests':
        return <SpecialRequestsSettingsSection {...shared} />;
      case 'clientManagement':
        return <ClientManagementSettingsSection {...shared} />;
      case 'emailNotifications':
        return <EmailNotificationsSettingsSection {...shared} />;
      default:
        return null;
    }
  };

  const renderContent = () => {
    if (MANAGER_SETTINGS_SECTIONS.includes(activeSection) && isManager) {
      return renderManagerSection();
    }

    return renderProfileSection();
  };

  return (
    <Layout settingsMode>
      <Box sx={{ maxWidth: 800, mx: 'auto', py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>{t('settings.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{activeCompany?.companyName}</Typography>
          </Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(isInternal ? '/dashboard' : '/inventory')} size="small">
            {t('settings.backToApp')}
          </Button>
        </Box>

        {renderContent()}
      </Box>
    </Layout>
  );
};

export default Settings;
